class PredictiveSearchComponent extends HTMLElement {
  constructor() {
    super();

    /** @type {HTMLInputElement | null} */
    this.input = this.querySelector('[ref="searchInput"]');
    /** @type {HTMLButtonElement | null} */
    this.closeButton = this.querySelector('[ref="closeButton"]');
    /** @type {HTMLElement | null} */
    this.resultsContainer = this.querySelector('[ref="results"]');
    /** @type {HTMLElement | null} */
    this.loadingIndicator = this.querySelector('[ref="loading"]');
    /** @type {HTMLFormElement | null} */
    this.searchForm = this.querySelector('form');
    /** @type {HTMLAnchorElement | null} */
    this.viewAllLink = this.querySelector('[ref="viewAllLink"]');

    /** @type {Map<string, { markup: string, totalCount: number }>} */
    this.cachedResults = new Map();
    /** @type {AbortController | null} */
    this.abortController = null;
    /** @type {number | null} */
    this.debounceTimer = null;
    this.isOpen = false;

    this.onInput = this.onInput.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.updatePanelHeight = this.updatePanelHeight.bind(this);
  }

  connectedCallback() {
    this.input?.addEventListener('input', this.onInput);
    this.closeButton?.addEventListener('click', this.onClose);
    this.addEventListener('keydown', this.onKeydown);
    this.searchForm?.addEventListener('submit', this.onFormSubmit);

    document.addEventListener('search:open', () => this.open());
    document.addEventListener('search:close', () => this.close());
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.onInput);
    this.closeButton?.removeEventListener('click', this.onClose);
    this.removeEventListener('keydown', this.onKeydown);
    this.searchForm?.removeEventListener('submit', this.onFormSubmit);
    window.visualViewport?.removeEventListener('resize', this.updatePanelHeight);

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  open() {
    this.isOpen = true;
    this.classList.add('is-open');
    const query = this.input?.value?.trim() || '';
    this.toggleQueryState(query);
    this.updateViewAllLink(query);
    this.input?.focus();
    document.body.style.overflow = 'hidden';
    this.updatePanelHeight();
    window.visualViewport?.addEventListener('resize', this.updatePanelHeight);
  }

  close() {
    this.isOpen = false;
    this.classList.remove('is-open');
    this.classList.remove('has-query');
    this.classList.remove('has-results');
    if (this.input) {
      this.input.value = '';
    }
    this.clearResults();
    this.updateResultsCount();
    this.updateViewAllLink();
    document.body.style.overflow = '';
    window.visualViewport?.removeEventListener('resize', this.updatePanelHeight);
    this.style.removeProperty('--search-panel-height');

    document.dispatchEvent(new CustomEvent('search:closed'));
  }

  /**
   * On iOS the keyboard shrinks the visual viewport only, so dvh keeps the
   * panel taller than what is actually visible and its bottom stays out of
   * reach under the keyboard.
   */
  updatePanelHeight() {
    const height = window.visualViewport?.height;
    if (height) this.style.setProperty('--search-panel-height', `${height}px`);
  }

  /** @param {KeyboardEvent} event */
  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  /** @param {Event} event */
  onFormSubmit(event) {
    const query = this.input?.value?.trim();
    if (!query) {
      event.preventDefault();
    }
  }

  /** @param {Event} event */
  onInput(event) {
    const target = /** @type {HTMLInputElement} */ (event.target);
    const query = target.value.trim();

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.toggleQueryState(query);
    this.updateViewAllLink(query);

    if (query.length === 0) {
      this.clearResults();
      this.updateResultsCount();
      this.classList.remove('has-results');
      return;
    }

    this.debounceTimer = setTimeout(() => {
      this.getSearchResults(query);
    }, 300);
  }

  onClose() {
    this.close();
  }

  clearResults() {
    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = '';
    }
  }

  /** @param {number} [count] */
  updateResultsCount(count = 0) {
    const countEl = this.querySelector('[ref="resultsCount"]');
    if (countEl) countEl.textContent = count > 0 ? String(count) : '';
  }

  /** @param {string} [query] */
  updateViewAllLink(query = '') {
    if (!this.viewAllLink) return;

    const searchUrl = Theme.routes.search_url;
    this.viewAllLink.href = query ? `${searchUrl}?q=${encodeURIComponent(query)}` : searchUrl;
  }

  /** @param {string} query */
  toggleQueryState(query) {
    this.classList.toggle('has-query', query.length > 0);
  }

  showLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.add('is-loading');
      this.loadingIndicator.setAttribute('aria-hidden', 'false');
    }
  }

  hideLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.remove('is-loading');
      this.loadingIndicator.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * Fetches predictive search results from Shopify API
   * @param {string} query - The search query
   */
  async getSearchResults(query) {
    if (this.cachedResults.has(query)) {
      const cachedResult = this.cachedResults.get(query);
      if (cachedResult) {
        const { markup, totalCount } = cachedResult;
        this.renderResults(markup, totalCount);
        return;
      }
    }

    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    this.showLoading();

    try {
      const searchUrl = Theme.routes.search_url;
      const [sectionResponse, countResponse] = await Promise.all([
        fetch(
          `${searchUrl}/suggest?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=hide&section_id=predictive-search`,
          { signal: this.abortController.signal }
        ),
        fetch(
          `${searchUrl}?q=${encodeURIComponent(query)}&type=product&options[unavailable_products]=hide&section_id=search-count`,
          { signal: this.abortController.signal }
        )
      ]);

      if (!sectionResponse.ok) {
        throw new Error(`Search failed: ${sectionResponse.status}`);
      }

      const text = await sectionResponse.text();
      const markup = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#shopify-section-predictive-search')?.innerHTML || '';

      let totalCount = 0;
      if (countResponse.ok) {
        try {
          const countMarkup = await countResponse.text();
          const countDocument = new DOMParser().parseFromString(countMarkup, 'text/html');
          const countElement = countDocument.querySelector('[data-results-count]');
          totalCount = parseInt(countElement?.getAttribute('data-results-count') || '0', 10);
        } catch {
          // Le compteur est optionnel, on garde les résultats même si son parsing échoue.
        }
      }

      this.cachedResults.set(query, { markup, totalCount });
      this.renderResults(markup, totalCount);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Predictive search error:', error);
        this.clearResults();
      }
    } finally {
      this.hideLoading();
    }
  }

  /**
   * Renders the search results in the container
   * @param {string} markup - HTML markup for results
   * @param {number} [totalCount] - Total number of results across all pages
   */
  renderResults(markup, totalCount = 0) {
    if (!this.resultsContainer) return;

    this.resultsContainer.innerHTML = markup;

    const resultsElement = /** @type {HTMLElement | null} */ (
      this.resultsContainer.querySelector('#predictive-search-results')
    );
    const resultsCount = parseInt(resultsElement?.dataset?.resultsCount || '0', 10);

    const displayCount = totalCount > 0 ? totalCount : resultsCount;
    this.updateResultsCount(displayCount);

    if (resultsCount > 0 || this.input?.value?.trim()) {
      this.classList.add('has-results');
    } else {
      this.classList.remove('has-results');
    }
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
